import React, { useId } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import BadgeImage from "./BadgeImage";
import {
  formatDate,
  learningJourneyPath,
  pickContinueEnrolment,
  pointsThisMonth,
  sortRewardsNewestFirst,
  totalPoints,
} from "./rewardsData";

const MAX_THUMBNAILS = 5;

const Tile = ({ label, className = "", children }) => {
  const labelId = useId();
  return (
    <div className={`lr-tile ${className}`} role="group" aria-labelledby={labelId}>
      <div className="lr-tile-label" id={labelId}>
        {label}
      </div>
      {children}
    </div>
  );
};

const PointsTile = ({ pointsHistory }) => {
  const intl = useIntl();
  const total = totalPoints(pointsHistory);
  const thisMonth = pointsThisMonth(pointsHistory);

  return (
    <Tile label={intl.formatMessage({ id: "my_rewards_points_earned" })}>
      <div className="lr-big">
        <span>{intl.formatNumber(total)}</span>{" "}
        <small>
          {intl.formatMessage({ id: "my_rewards_points_unit" }, { count: total })}
        </small>
      </div>
      {thisMonth > 0 && (
        <div className="lr-delta">
          {intl.formatMessage(
            { id: "my_rewards_points_this_month" },
            {
              points: intl.formatNumber(thisMonth),
              month: intl.formatDate(new Date(), { month: "long" }),
            }
          )}
        </div>
      )}
    </Tile>
  );
};

const BadgesTile = ({ rewards }) => {
  const intl = useIntl();
  const sorted = sortRewardsNewestFirst(rewards);
  const thumbnails = sorted.slice(0, MAX_THUMBNAILS);
  const more = sorted.length - thumbnails.length;

  return (
    <Tile label={intl.formatMessage({ id: "my_rewards_badges_earned" })}>
      <div className="lr-big">
        <span>{intl.formatNumber(sorted.length)}</span>{" "}
        <small>
          {intl.formatMessage(
            { id: "my_rewards_badges_unit" },
            { count: sorted.length }
          )}
        </small>
      </div>
      {sorted.length > 0 && (
        <div className="lr-mini">
          {thumbnails.map((reward) => (
            <BadgeImage
              key={reward.id}
              artifactUrl={reward.artifactUrl}
              alt={reward.rewardName}
              className="lr-mini-image"
              placeholderClassName="lr-mini-placeholder"
            />
          ))}
          {more > 0 && (
            <span className="lr-mini-more">
              <span aria-hidden="true">
                {intl.formatMessage({ id: "my_rewards_more_badges" }, { count: more })}
              </span>
              <span className="lr-sr-only">
                {intl.formatMessage(
                  { id: "my_rewards_more_badges_label" },
                  { count: more }
                )}
              </span>
            </span>
          )}
        </div>
      )}
    </Tile>
  );
};

const KeepGoingTile = ({ enrolment }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const name = enrolment.learningPathway?.name;
  const done = enrolment.completionCount?.completedCount || 0;
  const total =
    enrolment.totalLearningObjectCount || enrolment.completionCount?.totalCount || 0;
  const percent = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  // Optional training has no due date anywhere in the app.
  const due =
    enrolment.endDate && !enrolment.isOptional ? formatDate(intl, enrolment.endDate) : null;

  return (
    <Tile
      label={intl.formatMessage({ id: "my_rewards_keep_going" })}
      className="lr-next"
    >
      <h2 className="lr-next-name">{name}</h2>
      <div className="lr-meta">
        {intl.formatMessage(
          { id: due ? "my_rewards_progress_due" : "my_rewards_progress" },
          { done, total, date: due }
        )}
      </div>
      <div
        className="lr-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={intl.formatMessage({ id: "my_rewards_progress_label" }, { name })}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      <div className="lr-next-row">
        <span className="lr-meta">
          {intl.formatMessage({ id: "my_rewards_finish_to_earn" })}
        </span>
        <button
          type="button"
          className="lr-btn"
          onClick={() => navigate(learningJourneyPath(enrolment.id))}
        >
          {intl.formatMessage({ id: "my_rewards_continue" })}
        </button>
      </div>
    </Tile>
  );
};

const RewardsSummary = ({ rewards = [], pointsHistory = [], learningPrograms = [] }) => {
  const next = pickContinueEnrolment(learningPrograms);

  return (
    <div className={`lr-summary${next ? "" : " lr-summary--two"}`}>
      <PointsTile pointsHistory={pointsHistory} />
      <BadgesTile rewards={rewards} />
      {next && <KeepGoingTile enrolment={next} />}
    </div>
  );
};

export default RewardsSummary;
